import { reportRenderDiagnostic, clearRenderDiagnostic } from '@engine/renderDiagnosticsBus';
import { note, noteSet } from '@engine/memBudget';
import { noteDegrade } from '@engine/degradeRegistry';
import { BAKE_STRICT, bakeMissing } from '@engine/bakeStrict';
import { DEVICE_LIMITS } from '@/render/deviceProfile';
// 렌더 오프셋·복셀 격자 이동은 zero-import 상수 모듈이 SSOT — 워커(hfBuild)와 공유. 로컬 바인딩 확보 후 하단 re-export.
import { HF_RENDER_OFFSET, VOXEL_GRID_OFF, terrainBandY, roadSubFor, ROAD_STEP_TARGET_M, BAKED_STRATA, strataTerraceY } from './hfConstants';
import { maskAt, cellEdited } from './terrainEdits';
import { roadCellAt } from './roadCells';
import { CAVE_BITS, CAVE_STEP_UP, colBit, colLead, colCaveBelow } from './hfConstants';

// 이 안쪽(m)으로 지표 아래에 있는 발은 지표에 선 것으로 본다(캐릭터 공간 −0.05 를 덮는다).
const FEET_ON_TOP_EPS = 0.1;

// 하이트필드 PoC 데이터 공유 모듈 — 안③ P4 2단계.
// 렌더(HeightfieldPoC.jsx)와 발밑/충돌(terrainHandler.js)이 "같은 격자"를 읽어
// divergence 를 원천 차단한다(§5-C 렌더/발밑 동기화 원칙의 하이트필드 버전).
// 토글(showHfPoC) ON 이면 terrainHandler 의 getPartitionElevY/getPartitionFloorY 가
// 여기 이중선형(bilinear) 조회를 최우선으로 쓴다 — 캐릭터·몬스터·NPC·오브젝트·
// 카메라가 전부 그 두 함수를 경유하므로 자동으로 하이트필드 위를 걷는다.
// 커버리지 밖(다른 동)은 null → 기존 파티션 표면으로 자연 폴백.

// 2안 글로벌 단일맵: playable 전 파티션을 한 장의 8m 격자로 굽는다
// (bake_global_heightfield.py → seoul.global.hf.{json,bin}). 동간 이음매·검은띠가
// 구조적으로 없다. 발밑/충돌은 이 한 장을 이중선형 조회(hfSurfaceYAt 1 lookup),
// 렌더는 HeightfieldPoC 가 이 격자를 카메라 근처 고정 타일로 슬라이스한다.
export const GLOBAL_HF_KEY = 'seoul.global';

// ★ 2026-08-30 — 여기 있던 `HF_GROUPS`(동별 45타일 레지스트리)를 지웠다.
//   스스로 「(레거시) … 롬백/참고용」으로 적혀 있었고, 실제 사용처를 훑으니
//   **코드 참조가 0건**이었다(남은 한 줄은 :655 의 주석뿐이다).
//   런타임은 `GLOBAL_HF_KEY` 한 장만 받는다 — 동간 이음솔기가 없는 것이 그 단일맵의 이유다.
//   동별 타일로 되돌릴 일이 생기면 굽는 족(`bake_heightfield_group.cjs`)이 목록을 다시 만든다.
export { HF_RENDER_OFFSET, VOXEL_GRID_OFF }; // hfConstants SSOT re-export (상단 import 참조)

const cache = new Map(); // key -> 'loading' | 'missing' | { meta, heights: Float32Array|Uint16Array, dec }
const listeners = new Set();
let hfActive = false;

/** 지형 자료가 바뀌었을 때 부른다.
 *
 *  ★ 구독자는 **「어디가」 바뀌었는지를 인자로 받는다** (2026-08-27) —
 *    `rects` 는 월드 좌표 사각형 배열 `[{x0,z0,x1,z1}, ...]` 이고, **`null`·`undefined`
 *    이면 「전부 바뀌었다」**는 뜻이다(통짜 로드 완료·mip 도착·로드 실패).
 *    기존 구독자 넷은 전부 `() => setTick(t => t + 1)` 이라 인자를 안 본다 — 한 줄도 안 바뀐다.
 *
 *  [왜 필요했나] 받는 쪽이 「뭔가 바뀌었다」밖에 모르면 **가진 걸 전부 버릴 수밖에 없다.**
 *    `terrainChunkStore`(블록지형 밭)가 정확히 그랬다: 타일이 한 묶음 도착할 때마다 밭을
 *    통째로 버려서, 걸어 다니는 동안 **영원히 수렴하지 않았다**(그 파일 §타일 도착). */
export const onHfChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// ── ★ 「붙는 것」과 「바뀌는 것」을 알림에서 «가른다» ─────────────
// [무엇이 문제였나] 위 규약은 상태가 **둘**뿐이었다 — 사각형 목록이거나 `null`(전부).
//   그래서 «아무것도 안 틀려진» 사건(로드 실패·재시도 예약)까지 `null` 로 나갔고,
//   받는 밭들은 그걸 「밭 전체가 무효」로 읽어 **컬럼 1,200개를 통째로 버렸다**.
//   걸음 40초에 13~14회. 그때마다 `isComplete()` 가 거짓이 돼 원경 컷이 폴백 352m 로
//   주저앉는다(`0140_LOD밭_전량폐기_요동_진단.md`).
// [고친 규약 — 상태가 셋이다]
//   · `null`/`undefined` → **전부 바뀌었다**(전량 폐기). 정말 모를 때만.
//   · 비지 않은 배열      → **그 자리들이** 바뀌었다(사각형 폐기).
//   · **빈 배열 `[]`**    → **아무것도 안 틀려졌다.** 다시 그리기만 해라.
//     구독자 다섯은 전부 `setTick` 이라 그대로 리렌더하고, 밭 둘은 아무것도 안 버린다.
// ⚠ 빈 배열은 종전에 **나올 수 없는 값**이었다(`notifySoon` 은 사각형이 없으면 전량으로
//   떨어진다). 그래서 이 자리를 새 뜻으로 쓰는 게 안전하다 — 옛 뜻을 빼앗지 않는다.
const hfNotify = { tile: 0, index: 0, whole: 0, fail: 0, overflow: 0, all: 0, rectBatch: 0, rects: 0, none: 0 };
if (typeof window !== 'undefined') window.__hfNotify = hfNotify;
const notify = (rects, src) => {
  if (src) hfNotify[src] = (hfNotify[src] || 0) + 1;
  if (rects == null) hfNotify.all += 1;
  else if (rects.length) { hfNotify.rectBatch += 1; hfNotify.rects += rects.length; }
  else hfNotify.none += 1;
  listeners.forEach((fn) => fn(rects));
};

/** 자료 한 벌이 덮는 월드 사각형. 「이 키가 새로 붙었다」를 «자리»로 말하려고 쓴다. */
const metaRect = (meta) => ({
  x0: meta.origin_x, z0: meta.origin_z,
  x1: meta.origin_x + (meta.w - 1) * meta.cell,
  z1: meta.origin_z + (meta.h - 1) * meta.cell,
});

/** `?hfattach=0` — 위 「셋으로 가르기」를 끄고 **2026-08-30 이전 동작**으로 되돌린다.
 *  A/B 계측의 유일한 정직한 방법이다(워킹트리에 남의 미커밋이 상주하는 리포다). */
const HF_ATTACH = (() => {
  try { return new URLSearchParams(location.search).get('hfattach') !== '0'; } catch { return true; }
})();

// ── 타일 도착 알림은 **한 프레임으로 묶는다** ─────────────────────
// `onHfChange` 구독자가 다섯이다 — HeightfieldPoC · WorldRivers · FarTerrain ·
// PartitionBoundaryOverlay · SeoulDistrictOverlay. 전부 `setTick(t => t + 1)` 이라
// **알림 1회 = React 리렌더 5회 = 지형·강·원경 재빌드**다. 그런데 종전엔 타일이
// **한 장 도착할 때마다** 그걸 동기로 때렸다. 동시 fetch 가 6이니 한 번에 6연타다.
// 이게 「타일당 1.86MB」의 실체이고, 폰에서 초당 7장이면 초당 35회 재빌드가 된다.
//
// ⚠ 합치는 건 **타일 도착뿐**이다. 통짜 로드 완료·mip 도착은 그대로 즉시 알린다 —
//   거기엔 순서 의존이 있다(WorldRivers §1026: 알림이 HeightfieldPoC 의 활성화보다
//   먼저 오면 수면 높이가 null 로 굳는다). 늦추는 건 안전하지만 **경로를 바꾸진 않는다.**
//
// ★ 묶으면서 **「어느 자리가」 왔는지도 같이 모은다**. 합집합 사각형 하나로
//   접지 않고 **목록**으로 든다 — 한 프레임에 오는 타일이 사방에 흩어져 있으면 합집합은
//   그 사이 빈 땅까지 삼켜 결국 「전부」와 같아진다. 목록이 길어지면(동시 fetch 6 이므로
//   보통 6 이하) 그때는 접는 게 싸므로 상한을 넘기면 «전량»으로 떨어뜨린다.
const NOTIFY_RECT_MAX = 12;
let notifyQueued = false;
let notifyRects = [];
let notifyAll = false;       // 사각형을 못 정한 알림이 섞였다 — 목록은 의미가 없어진다
const notifySoon = (rect) => {
  if (!rect) notifyAll = true;
  else if (!notifyAll) {
    notifyRects.push(rect);
    if (notifyRects.length > NOTIFY_RECT_MAX) { notifyAll = true; notifyRects = []; hfNotify.overflow += 1; }
  }
  if (notifyQueued) return;
  notifyQueued = true;
  const run = () => {
    notifyQueued = false;
    const rects = notifyAll ? null : notifyRects;
    notifyRects = []; notifyAll = false;
    notify(rects, 'tile');
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else setTimeout(run, 16);
};

// ── 재시도 ────────────────────────────────────────────────────────
// 예전엔 한 번 실패하면 `cache.set(key,'missing')` 이 그대로 무덤이 됐다 — `cache.has(key)`
// 가 true 라 **그 탭은 세션 내내 지형이 없다.** 8/18 실유저 사고 9건이 전부 이 모양이었다
// (건물만 뜨고 땅이 없는 화면). 74MB 연속 할당은 힙이 파편화된 순간에만 거절되므로,
// 몇 초 뒤 다시 받으면 대개 성공한다.
// ⚠ 무한 재시도는 금지 — 74MB 를 계속 두드리면 할당 압력이 오히려 커진다. 상한 3회 + 백오프.
const RETRY_DELAYS = [2000, 6000, 15000];
const retryCount = new Map();

const hfFail = (key, detail) => {
  const n = retryCount.get(key) || 0;
  const delay = RETRY_DELAYS[n];
  if (delay == null) {
    cache.set(key, 'missing');                       // 상한 소진 — 여기서 멈춘다
    reportRenderDiagnostic({ phase: 'data-error', detail: `${detail} — 재시도 ${RETRY_DELAYS.length}회 모두 실패` });
    // ★ **아무것도 안 틀려졌다.** 이 키는 애초에 한 번도 값을 준 적이 없다(`hfFail` 은
    //   로드 경로에서만 불린다 — 캐시는 'loading' 이었다). 이미 만든 컬럼은 이 키 없이
    //   만든 것이라 여전히 맞다. 진단만 다시 그리게 하고 밭은 건드리지 않는다.
    notify(HF_ATTACH ? [] : undefined, 'fail');
    return;
  }
  retryCount.set(key, n + 1);
  cache.delete(key);                                 // 'missing' 을 남기지 않는다(남기면 무덤이 된다)
  reportRenderDiagnostic({ phase: 'data-error', detail: `${detail} — ${delay / 1000}초 후 재시도 (${n + 1}/${RETRY_DELAYS.length})` });
  // 호출자가 다시 불러 줄 거라 기대하지 않는다 — 스스로 되살아나야 복구가 보장된다.
  setTimeout(() => { if (!cache.has(key)) ensureHfLoaded(key); }, delay);
  notify(HF_ATTACH ? [] : undefined, 'fail');       // 위 ★ 와 같은 이유 — 재시도 «예약»일 뿐이다
};

// ── Uint16 고정소수점 디코드 ──────────────────────────────────────
// `.hf.json` 에 `h_enc:'u16'` 이 있으면 `.hf.u16.bin`(35MB)을 Uint16 **그대로** 상주시키고
// 소비 지점에서만 실수로 되돌린다. Float32Array 로 변환해 버리면 70MB 가 그대로라 절약이 0 이다.
// 센티널(65535)을 **NaN 으로 되돌려 주므로** 기존 `Number.isNaN`/`isFinite` 검사는 한 줄도 안 바뀐다.
export const HF_NAN_U16 = 0xFFFF;
const decoderFor = (meta) => {
  if (meta?.h_enc !== 'u16') return (v) => v;        // Float32 원본 — 항등(하위 호환)
  const min = meta.h_min, s = meta.h_scale;
  return (v) => (v === HF_NAN_U16 ? NaN : min + v * s);
};

// ── 타일 모드 — 「서울 전체를 모두에게 통째로」를 끝낸다 ─────────────
// [무엇이 문제였나] u16 파생물로 74MB 를 35MB 로 줄였지만 **여전히 한 장**이라, 강남에
//   서 있든 후암동에 서 있든 전 유저가 4880×3795 전량을 받고 탭 수명 내내 들고 있었다.
//   실측: 와이어 gzip 14.6MiB · 상주 35.3MiB · 그런데 유효 셀은 50.8% 뿐이고 한 세션이
//   실제로 밟는 넓이는 그 중 몇 % 다.
//
// [형태] `bake_hf_tiles.py` 가 굽는 것 셋 —
//   ① 타일 `tiles/<key>/t<tx>_<tz>.bin` — **129×129 u16**(32KiB). 격자는 렌더의
//      `HeightfieldPoC.TILE = 128`(1024m)과 **같다** → 렌더 타일 1장 = 저장 타일 1장.
//      1셀 겹쳐 굽는 이유: 렌더 창이 TILE+1 이고 발밑이 셀 4코너를 읽으므로, 겹치면
//      **둘 다 언제나 한 타일 안에서 끝난다**(안 겹치면 메시 한 장에 저장 타일 4장).
//   ② mip `<key>.mip8.avg.bin` — 64m 평균. **타일이 오기 전 발밑이 딛는 값**이자
//      원경 지형(FarTerrain) 64m 블록 기둥의 높이. 발밑 오차 중앙 1.00m · 90% 5.46m.
//
// [왜 안전한가] 옛 통짜 경로를 **한 줄도 안 지웠다.** 인덱스(`<key>.tiles.json`)가 404 면
//   자동으로 옛 경로를 타고, `?hftiles=0` 으로 손으로도 끌 수 있다. 되돌리기 = 플래그 한 줄.
export const HF_TILE = 128;                 // ⚠ HeightfieldPoC.TILE 과 같은 값이어야 한다
const HF_TILE_MAX = 96;                     // 상주 상한(96 × 32KiB ≈ 3MiB). 넘으면 먼 것부터 버린다
// 동시 fetch 수와 초당 발주 상한 — **기기 프로필이 정한다**(deviceProfile.js PROFILE_LIMITS).
// PC 는 6 / 무제한으로 종전과 완전히 같고, 폰만 조인다. 근거는 그쪽 주석에 있다.
const HF_TILE_PAR = DEVICE_LIMITS.hfTilePar ?? 6;
const HF_TILE_RATE = DEVICE_LIMITS.hfTileRate ?? 0;   // 0 = 무제한

const tilesEnabled = () => {
  try {
    const q = new URLSearchParams(location.search).get('hftiles');
    if (q === '0' || q === 'off') return false;
  } catch { /* SSR·비브라우저 */ }
  return true;
};

// 타일 (tx,tz) 의 실제 크기 — 굽는 쪽(bake_hf_tiles.tile_dims)과 **같은 식**이라 안 적는다.
const tileW = (meta, tx) => Math.min(HF_TILE + 1, meta.w - tx * HF_TILE);
const tileH = (meta, tz) => Math.min(HF_TILE + 1, meta.h - tz * HF_TILE);

// 타일 한 장이 «월드에서» 덮는 사각형. 소비처가 「내 것 중 어디를 버릴까」를 정하는 데 쓴다.
//
// [왜 이 식이 맞나] `hfSurfaceYAt` 은 `ix = floor((wx - gOff - origin_x) / cell)` 로 격자를
//   잡고, `cell4` 는 그 셀의 네 코너를 **한 타일 안에서만** 읽는다(1셀 겹쳐 굽는 이유).
//   즉 타일 (tx,tz) 가 답을 바꾸는 자리는 격자 ix ∈ [tx·128, tx·128 + tw − 1] 뿐이다.
// ⚠ 반 칸 이동(`VOXEL_GRID_OFF` 0.5m)과 도로정지 패딩(R, 기본 12m)은 여기서 안 뺀다 —
//   소비처가 자기 여유(`HF_RECT_PAD`)를 얹는다. 좁게 잡는 실수는 **조용히 틀린 지형**으로
//   남지만, 넓게 잡는 실수는 조각 몇 개를 더 만드는 값으로 끝난다.
const tileRect = (meta, tx, tz) => ({
  x0: meta.origin_x + tx * HF_TILE * meta.cell,
  z0: meta.origin_z + tz * HF_TILE * meta.cell,
  x1: meta.origin_x + (tx * HF_TILE + tileW(meta, tx) - 1) * meta.cell,
  z1: meta.origin_z + (tz * HF_TILE + tileH(meta, tz) - 1) * meta.cell,
});

/** 굽기 누락으로 이 키를 멈춘다(엄격 모드) — 재시도하지 않는다(404 는 기다려도 안 생긴다). */
const hfBakeStop = (key, id, what, fix) => {
  bakeMissing(id, what, fix);
  cache.set(key, 'missing');
  reportRenderDiagnostic({ phase: 'data-error', detail: `[굽기 누락] ${what} — 구우십시오: ${fix}` });
  notify(HF_ATTACH ? [] : undefined, 'fail');
};

/** 타일 인덱스가 없거나 낡았다. 엄격 모드면 멈추고, 아니면 통짜 35.3MB 로 내려간다(옛 동작). */
const wholeFallbackOrStop = (key, why) => {
  const fix = 'python back/scripts/bake_hf_tiles.py';
  if (BAKE_STRICT) { hfBakeStop(key, 'bake:hf:tiles', `지형 타일 인덱스 — ${why}`, fix); return; }
  noteDegrade('bake:hf:whole', {
    what: `지형을 타일이 아니라 «통짜»로 받습니다 — ${why}`,
    cost: '와이어 gzip 14.6MiB · 상주 35.3MiB 연속 할당',
    fix,
  });
  loadWhole(key);
};

// ── 지역 색인 — 서울 한 장 뒤에 회랑 지역들을 같은 방식(타일)으로 붙인다 ──────────
// `regions.json`(bake_regions_index.py)의 순서가 곧 발밑 조회 우선순위다. cache 는 Map 이라
// 넣은 순서대로 돌고, `hfSurfaceYAt` 은 앞 지역이 못 답한(빈칸) 자리만 다음 지역에 묻는다.
// 404 면 서울 한 장으로 끝난다(종전과 같다).
let regionsAsked = false;
let regionBoxes = [];        // 회랑 지역의 월드 사각형 — 지층 단구 지역 규칙(REGION_STRATA)이 본다
const loadRegions = () => {
  if (regionsAsked) return;
  regionsAsked = true;
  fetch('/terrain_baked/regions.json').then((r) => (r.ok ? r.json() : null)).then((j) => {
    const rs = (j?.regions || []).filter((r) => r.key !== GLOBAL_HF_KEY);
    regionBoxes = rs.map((r) => ({ x0: r.origin_x, z0: r.origin_z,
      x1: r.origin_x + (r.w - 1) * r.cell, z1: r.origin_z + (r.h - 1) * r.cell }));
    for (const r of rs) ensureHfLoaded(r.key);
  }).catch(() => { /* 색인이 없으면 서울만 */ });
};

/** 지금 타일 모드로 올라온 지형 키들(서울 먼저). 렌더가 키마다 타일을 고를 때 쓴다. */
export const getHfTiledKeys = () => {
  const out = [];
  for (const [k, e] of cache) if (e && typeof e !== 'string' && e.mode === 'tiled') out.push(k);
  return out;
};

export const ensureHfLoaded = (key) => {
  if (key === GLOBAL_HF_KEY) loadRegions();
  if (cache.has(key)) return;
  cache.set(key, 'loading');
  // 타일 인덱스를 **먼저** 친다. 있으면 타일 모드, 404 면 옛 통짜 경로(아래)로 간다.
  // ⚠ 404 만 폴백이다 — 그 외 HTTP 오류는 「아직 안 구웠다」가 아니라 「지금 실패했다」라서
  //   폴백하면 35MB 연속 할당을 일시적 오류 한 번에 되살린다(u16 전환 때 배운 것).
  if (!tilesEnabled()) { loadWhole(key); return; }
  fetch(`/terrain_baked/${key}.tiles.json`).then(async (r) => {
    if (r.status === 404) { wholeFallbackOrStop(key, `/terrain_baked/${key}.tiles.json 이 없습니다(404)`); return; }
    if (!r.ok) throw new Error(`tiles.json HTTP ${r.status}`);
    const idx = await r.json();
    if (idx?.ver !== 1) {                             // 모르는 형식 — 통짜가 언제나 진실이다
      wholeFallbackOrStop(key, `형식을 모릅니다(ver=${idx?.ver}) — 낡은 굽기`); return;
    }
    await loadTiled(key, idx);
  }).catch((e) => {
    hfFail(key, `지형 타일 인덱스 로드 실패${e?.message ? ': ' + String(e.message).slice(0, 60) : ''}`);
  });
};

// ── 지층 단구 마스크 — 16m 칸 비트맵. `bake_strata_mask.py` 가 굽는다 ──
// 판 0 = 단구 금지, 판 1(v2) = 명소 종전식(vary=0). 판 1 은 plane_bytes 뒤에 붙는다.
// 하이트필드와 «같이» 받아 cache 에 올리기 전에 끝낸다 — 늦게 오면 이미 지은 지형과 발밑이 갈린다.
// 없으면 단구는 통째로 꺼진다(지문 strata=0 → 그 상태로 구운 LOD 와 맞는다).
let strataMask = null;
let strataLoad = 'idle';   // 'idle'(아직 안 받음) · 'loading' · 'done'
const strataParams = (() => {
  if (typeof window === 'undefined') return BAKED_STRATA;
  const v = new URLSearchParams(window.location.search).get('tshape');
  return v === '0' ? { ...BAKED_STRATA, on: 0 } : BAKED_STRATA;
})();
const strataLegacyParams = { ...strataParams, vary: 0 };
const loadStrataMask = async (key) => {
  // 마스크는 서울 한 장 크기 하나뿐이다 — 회랑 지역 키가 덮어쓰거나(dev 서버는 없는 파일에
  // index.html 을 200 으로 준다) 지우면 서울 LOD 지문(strata)이 0 이 되어 구운 LOD 전체가 꺼진다.
  if (!strataParams.on || key !== GLOBAL_HF_KEY) return;
  strataLoad = 'loading';
  try {
    const [hdr, buf] = await Promise.all([
      fetch(`/terrain_baked/${key}.strata.json`).then((r) => (r.ok ? r.json() : null)),
      fetch(`/terrain_baked/${key}.strata.bin`).then((r) => (r.ok ? r.arrayBuffer() : null)),
    ]);
    if (!hdr || !buf || buf.byteLength * 8 < hdr.w * hdr.h) return;
    const bits = new Uint8Array(buf);
    const pb = hdr.plane_bytes | 0;
    const legacy = hdr.planes >= 2 && pb > 0 && buf.byteLength >= pb * 2 ? bits.subarray(pb, pb * 2) : null;
    strataMask = { ...hdr, bits, legacy };
  } catch { strataMask = null; } finally { strataLoad = 'done'; }
};
/** lodBake.verify 가 strata 지문을 «마스크 도착 전 0»으로 오판하지 않게 묻는다. */
export const isHfStrataSettled = () => !strataParams.on || strataLoad === 'done';
const strataBit = (m, i, j) => {
  if (i < 0 || j < 0 || i >= m.h || j >= m.w) return 1;
  const b = i * m.w + j;
  return (m.bits[b >> 3] >> (b & 7)) & 1;
};
/** 회랑 지역(서울 마스크 밖)의 단구 규칙 판번호 — 물·도로·시공물이 없어 전부 허용한다.
 *  서울 마스크 지문(getHfStrataSig)에는 안 넣는다(넣으면 서울 LOD 가 통째로 낡는다).
 *  대신 지역 LOD 매니페스트가 `region_strata` 로 싣고 lodBake 가 대조한다. 규칙을 바꾸면 올린다. */
export const REGION_STRATA = 'all-v1';
const inRegionBox = (x, z) => {
  for (const b of regionBoxes) if (x >= b.x0 && x <= b.x1 && z >= b.z0 && z <= b.z1) return true;
  return false;
};
/** 0~1 — 제외 칸이면 0, 이웃 8칸 중 제외 칸 비율만큼 깎아 16m 에 걸쳐 잦아든다. */
const strataAmtAt = (x, z) => {
  const m = strataMask;
  if (!m) return 0;
  const j = Math.floor((x - m.origin_x) / m.cell), i = Math.floor((z - m.origin_z) / m.cell);
  if ((i < 0 || j < 0 || i >= m.h || j >= m.w) && inRegionBox(x, z)) return 1;
  if (strataBit(m, i, j)) return 0;
  let n = 0;
  for (let di = -1; di <= 1; di++) for (let dj = -1; dj <= 1; dj++) if (di || dj) n += strataBit(m, i + di, j + dj);
  return 1 - n / 8;
};
/** (x,z) 자리의 단구 매개변수 — 명소(판 1)면 종전식 한 벌, 아니면 지역별 다각화. */
const strataParamsAt = (x, z) => {
  const m = strataMask;
  if (!m || !m.legacy) return strataParams;
  const j = Math.floor((x - m.origin_x) / m.cell), i = Math.floor((z - m.origin_z) / m.cell);
  if (i < 0 || j < 0 || i >= m.h || j >= m.w) return strataParams;
  const b = i * m.w + j;
  return (m.legacy[b >> 3] >> (b & 7)) & 1 ? strataLegacyParams : strataParams;
};
/** 원경용 — 월드 Y 높이 y 에 (x,z) 자리의 지층 단구를 입힌다. 마스크가 없거나 꺼졌으면 null(=원경은 단구를 안 탄다). */
export const getHfStrataFn = () => (strataParams.on && strataMask
  ? { at: (y, x, z, slope) => strataTerraceY(y, x, z, slope, strataAmtAt(x, z), strataParamsAt(x, z)),
      slopeLo: strataParams.slopeLo }
  : null);
/** 지문용 — 켜짐·매개변수·마스크가 같으면 같은 수. 꺼졌거나 마스크가 없으면 0. */
export const getHfStrataSig = () => {
  if (!strataParams.on || !strataMask) return 0;
  const s = JSON.stringify(strataParams) + strataMask.sig;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return h;
};

// ── 타일 모드 적재: 인덱스 + mip 두 장. 타일 자체는 카메라를 알게 된 뒤에 받는다 ──
const loadTiled = async (key, idx) => {
  const [avg, hdr] = await Promise.all([
    fetch(`/terrain_baked/${key}.mip${idx.mip_stride}.avg.bin`).then((r) => (r.ok ? r.arrayBuffer() : null)),
    // ★ `.hf.json` 은 타일 모드에서도 **그대로 받는다.**
    //   처음엔 인덱스가 격자 정보를 다 들고 있으니 안 받으려 했는데, 그 파일 409KiB 중
    //   **408KiB 가 `river_bed`** 다 — 수면 높이(`levels`)·수로 링(`channel_rings`)이고
    //   `WorldRivers` 가 `meta.river_bed` 로 읽는다. 안 받으면 지형은 뜨는데 **강이 조용히
    //   틀린 높이로 선다.** 「헤더는 격자 정보다」라는 짐작이 틀렸던 자리다.
    //   대신 이건 압축이 잘 든다(gzip 121KiB) — 35.3MiB 를 줄이는 값으로는 싸다.
    fetch(`/terrain_baked/${key}.hf.json`).then((r) => (r.ok ? r.json() : null)),
    loadStrataMask(key),
  ]);
  if (!avg) { hfFail(key, `지형 mip 로드 실패 (${key}.mip${idx.mip_stride}.avg.bin)`); return; }
  // 헤더가 없으면 `river_bed` 가 빠져 강이 틀린 높이로 선다 — 계산으로 메우지 않고 신고한다.
  if (!hdr) bakeMissing(`bake:hf:hdr:${key}`, `지형 헤더 /terrain_baked/${key}.hf.json 을 못 받았습니다 — 강 수면(river_bed)이 빠집니다`,
    'python back/scripts/bake_hf_tiles.py');
  // 헤더가 원본이고 인덱스가 덮어쓴다 — 격자 파라미터는 굽기 시점이 같아 값이 같지만,
  // 갈렸다면 **타일을 실제로 자른 쪽**(인덱스)이 옳다.
  const meta = {
    ...(hdr || {}),
    group_key: key, w: idx.w, h: idx.h, cell: idx.cell, base_y: idx.base_y,
    origin_x: idx.origin_x, origin_z: idx.origin_z,
    h_enc: idx.h_enc, h_min: idx.h_min, h_scale: idx.h_scale, h_nan: idx.h_nan,
  };
  note(`hf.mip:${key}`, avg.byteLength);
  cache.set(key, {
    meta,
    mode: 'tiled',
    dec: decoderFor(meta),
    heights: null,                                   // 통짜 배열이 **없다**는 게 이 모드의 정의
    tiles: new Map(),                                // tid → { a:Uint16Array, tw, th, seq }
    pending: new Set(),
    queue: [],
    inflight: 0,
    seq: 0,
    present: idx.present,
    txN: idx.tx_n, tzN: idx.tz_n,
    mip: {
      avg: new Uint16Array(avg),
      w: idx.mip_w, h: idx.mip_h, stride: idx.mip_stride,
    },
  });
  retryCount.delete(key);
  clearRenderDiagnostic('data-error');
  // ★ 「자리」로 말한다 — 이 키가 덮는 사각형이 곧 「새로 붙은 곳」이다(위 §셋으로 가르기).
  //   지금은 런타임이 `seoul.global` 하나만 부르므로 그 사각형이 세계 전부이고, 결과는
  //   전량 폐기와 같다(기동 때 한 번). 동별 키가 다시 생기면 이 자리가 그때 값을 한다.
  notify(HF_ATTACH ? [metaRect(cache.get(key).meta)] : undefined, 'index');
};

const tidOf = (e, tx, tz) => tz * e.txN + tx;
export const hfTilePresent = (e, tx, tz) =>
  tx >= 0 && tz >= 0 && tx < e.txN && tz < e.tzN && e.present[tidOf(e, tx, tz)] === '1';
export const getHfTile = (e, tx, tz) => e.tiles?.get(tidOf(e, tx, tz)) || null;

// ── 발주 속도 제한 (토큰 버킷) ────────────────────────────────────────────────
// 동시 fetch 상한(`HF_TILE_PAR`)만으로는 **속도를 못 막는다** — 한 장이 끝나면 `finally`
// 가 즉시 다음 장을 당겨오므로, 망이 빠를수록 더 빨리 삼킨다. 폰에서 문제가 되는 축은
// 「동시에 몇 개」가 아니라 **「초당 몇 장」**이라 상한을 따로 둔다.
let rateTokens = HF_TILE_RATE;
let rateAt = 0;
let rateTimer = 0;
const takeTileToken = (key) => {
  if (!HF_TILE_RATE) return true;                    // PC — 종전 그대로
  const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  if (rateAt) rateTokens = Math.min(HF_TILE_RATE, rateTokens + ((now - rateAt) / 1000) * HF_TILE_RATE);
  rateAt = now;
  if (rateTokens >= 1) { rateTokens -= 1; return true; }
  // 토큰이 없으면 **버리지 않고 미룬다** — 큐는 그대로 두고 다음 채워질 때 다시 편다.
  if (!rateTimer) {
    const waitMs = Math.ceil(((1 - rateTokens) / HF_TILE_RATE) * 1000);
    rateTimer = setTimeout(() => { rateTimer = 0; pumpTiles(key); }, waitMs);
  }
  return false;
};

const pumpTiles = (key) => {
  const e = cache.get(key);
  if (!e || typeof e === 'string' || e.mode !== 'tiled') return;
  while (e.inflight < HF_TILE_PAR && e.queue.length) {
    if (!takeTileToken(key)) break;
    const tid = e.queue.shift();
    const tx = tid % e.txN, tz = (tid / e.txN) | 0;
    e.inflight += 1;
    fetch(`/terrain_baked/tiles/${key}/t${tx}_${tz}.bin`).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const buf = await r.arrayBuffer();
      const tw = tileW(e.meta, tx), th = tileH(e.meta, tz);
      const a = new Uint16Array(buf);
      // 인덱스가 「있다」고 한 타일이 크기가 다르면 **조용히 틀린 지형**이 된다 — 굽기와
      // 인덱스가 갈린 상태다. 그리지 않고 없는 것으로 둔다(mip 폴백이 받는다).
      if (a.length !== tw * th) throw new Error(`크기 ${a.length} ≠ ${tw * th} (인덱스와 갈린 낡은 굽기)`);
      e.tiles.set(tid, { a, tw, th, seq: e.seq++ });
      notifySoon(tileRect(e.meta, tx, tz));          // ★ 한 프레임으로 묶는다 + «어디가» 바뀌었나(위 주석)
    }).catch((err) => {
      // 한 장이 실패해도 지형 전체를 죽이지 않는다 — 그 자리만 mip 으로 그린다(오차 최대 119m).
      // present 에서 지워 다시 안 조르게 한다(무한 재요청이 더 나쁘다). 조용히 넘기지 않고 신고한다.
      bakeMissing(`bake:hf:t${tx}_${tz}`, `지형 타일 t${tx}_${tz} — ${String(err?.message || err).slice(0, 80)} · 그 자리는 mip(오차 최대 119m)으로 그립니다`,
        'python back/scripts/bake_hf_tiles.py');
      const p = e.present.split(''); p[tid] = '0'; e.present = p.join('');
    }).finally(() => {
      e.pending.delete(tid);
      e.inflight -= 1;
      pumpTiles(key);
    });
  }
};

/**
 * 카메라 주변 타일을 받아 두고, 멀어진 타일은 버린다.
 * `ids` 는 `"tx_tz"` 문자열 배열 — 렌더의 `activeTiles` 를 그대로 넘긴다(격자가 같다).
 * 통짜 모드에서는 아무 일도 안 한다(전량이 이미 있다).
 */
export const ensureHfTiles = (key, ids, center = null) => {
  const e = cache.get(key);
  if (!e || typeof e === 'string' || e.mode !== 'tiled' || !ids?.length) return;
  const keep = new Set();
  const fresh = [];
  for (const id of ids) {
    const s = id.indexOf('_');
    const tx = +id.slice(0, s), tz = +id.slice(s + 1);
    if (!hfTilePresent(e, tx, tz)) continue;          // 빈 땅 타일 447장은 요청 자체를 안 한다
    const tid = tidOf(e, tx, tz);
    keep.add(tid);
    const cur = e.tiles.get(tid);
    if (cur) { cur.seq = e.seq++; continue; }         // 최근 사용 도장
    if (e.pending.has(tid)) continue;
    e.pending.add(tid); fresh.push([tid, tx, tz]);
  }
  // ── 발밑부터 받는다 ────────────────────────────────────────────────────────
  // 순서가 없으면 목록 순(= 좌상단부터)으로 받는다. 첫 진입에 수십 장이 한꺼번에 뜨는데
  // 하필 **내가 서 있는 칸이 마지막**일 수 있고, 그동안 발밑은 mip(오차 중앙 1m·최대 119m)이다.
  // 절벽 위 스폰에서 그 최대치를 밟으면 캐릭터가 눈에 띄게 뜬다 → 가까운 것부터.
  if (center && fresh.length > 1) {
    const { meta } = e;
    const cx = (center[0] - meta.origin_x) / meta.cell / HF_TILE;
    const cz = (center[1] - meta.origin_z) / meta.cell / HF_TILE;
    fresh.sort((a, b) => ((a[1] - cx) ** 2 + (a[2] - cz) ** 2) - ((b[1] - cx) ** 2 + (b[2] - cz) ** 2));
  }
  for (const [tid] of fresh) e.queue.push(tid);
  // ── 축출 — 상주 상한을 넘으면 **가장 오래 안 쓴 것부터** ─────────────────────
  // keep 에 든 것은 절대 안 버린다(지금 그리고 있고, 발밑이 딛고 있다).
  if (e.tiles.size > HF_TILE_MAX) {
    const cands = [...e.tiles.entries()].filter(([tid]) => !keep.has(tid)).sort((a, b) => a[1].seq - b[1].seq);
    for (let i = 0; i < cands.length && e.tiles.size > HF_TILE_MAX; i += 1) e.tiles.delete(cands[i][0]);
  }
  // ★ 여기는 「그때그때의 총량」을 다시 알려 오는 자리다 — `note`(누적)를 쓰면 부른 횟수만큼
  //   더해져 실측 0.6MB 가 원장 14.9MB 로 찍혔다. `noteSet` 은 덮어쓴다.
  noteSet(`hf.tiles:${key}`, e.tiles.size * (HF_TILE + 1) * (HF_TILE + 1) * 2);
  pumpTiles(key);
};


// ── 옛 통짜 경로 — 인덱스가 없거나 `?hftiles=0` 일 때. **한 줄도 안 바꿨다.** ──────
const loadWhole = (key) => {
  // u16 파생물을 **먼저·병렬로** 친다. meta 를 기다렸다 URL 을 고르면 왕복이 하나 늘어
  // 지형이 그만큼 늦게 뜬다.
  //
  // ⚠ **f32 폴백은 404 일 때만이다.** 예전엔 u16 이 어떤 이유로든 실패하면 곧장 f32(70.6MB)를
  //   집으러 갔는데, 그건 이 과업이 없애려던 바로 그 연속 할당을 일시적 오류 한 번에 되살린다.
  //   404 = 「그 저장소는 아직 u16 을 안 구웠다」(구조적) · 그 외 = 「지금 실패했다」(일시적)
  //   → 후자는 폴백이 아니라 **재시도**가 답이다.
  const jsonP = fetch(`/terrain_baked/${key}.hf.json`).then((r) => (r.ok ? r.json() : null));
  const binP = (async () => {
    const r = await fetch(`/terrain_baked/${key}.hf.u16.bin`);
    if (r.ok) return { buf: await r.arrayBuffer(), u16: true };
    if (r.status !== 404) throw new Error(`u16 HTTP ${r.status}`);
    if (BAKE_STRICT) {
      hfBakeStop(key, 'bake:hf:u16', `압축 지형 /terrain_baked/${key}.hf.u16.bin 이 없습니다(404) — f32 원본 70.6MB 로 내려가지 않습니다`,
        'python back/scripts/compress_hf_u16.py');
      return 'stopped';
    }
    noteDegrade('bake:hf:f32', {
      what: 'u16 파생물이 없어 f32 원본을 받습니다',
      cost: '70.6MB 연속 할당(u16 의 2배)',
      fix: 'python back/scripts/compress_hf_u16.py',
    });
    const f = await fetch(`/terrain_baked/${key}.hf.bin`);   // 아직 안 구운 저장소용
    return f.ok ? { buf: await f.arrayBuffer(), u16: false } : null;
  })();
  Promise.all([jsonP, binP, loadStrataMask(key)]).then(([meta, got]) => {
    if (got === 'stopped') return;
    if (!meta || !got?.buf) {
      // 하이트필드(지형의 유일한 소스, LAYER_DEFAULTS.showHfPoC)가 로드 실패 —
      // 예전엔 무음이라 배너에 "정체(원인불명)"로만 뜨고 진짜 이유가 안 보였음.
      hfFail(key, `지형 하이트필드 로드 실패 (${key}.hf.json/.bin 404 또는 빈 응답)`);
      return;
    }
    // 굽기와 파생물이 갈라졌을 때 조용히 쓰레기 지형을 그리지 않는다.
    const wantU16 = meta.h_enc === 'u16';
    if (wantU16 !== got.u16) {
      hfFail(key, `지형 하이트필드 인코딩 불일치 (meta=${meta.h_enc || 'f32'} / bin=${got.u16 ? 'u16' : 'f32'})`);
      return;
    }
    // ★ 전역 하이트필드는 **단일 최대 상주물**이다 — u16 35MB(4880×3795×2, 예전 f32 는 74MB).
    //   이 Map 에는 evict 가 없어 탭 수명 내내 산다. 원장에 얹지 않으면 할당 실패 점유표에서
    //   「지형 타일이 제일 크다」로 보이지만 실제 자리를 먹고 있는 건 이 한 장이다.
    note(`hf.global:${key}`, got.buf.byteLength);
    cache.set(key, {
      meta,
      mode: 'whole',
      heights: got.u16 ? new Uint16Array(got.buf) : new Float32Array(got.buf),
      dec: decoderFor(meta),
    });
    retryCount.delete(key);
    clearRenderDiagnostic('data-error');
    notify(HF_ATTACH ? [metaRect(meta)] : undefined, 'whole');   // 위 §셋으로 가르기
  }).catch((e) => {
    hfFail(key, `지형 하이트필드 로드 실패 (네트워크)${e?.message ? ': ' + String(e.message).slice(0, 60) : ''}`);
  });
};

export const getHfEntry = (key) => cache.get(key);

// ── 셀 4코너 읽기 — **통짜/타일/mip 을 여기 한 곳에서만 가른다** ─────────────────
// [왜 한 곳인가] 렌더와 발밑이 갈리는 사고를 이 프로젝트는 세 번 겪었다(SOP §5-C).
//   폴백을 소비 지점마다 쓰면 「렌더는 타일, 발밑은 mip」 같은 조합이 생겨 같은 자리에서
//   두 높이가 나온다. 그래서 **누가 묻든 같은 함수가 답한다.**
// mip 폴백은 타일이 아직 안 온 잠깐 동안만 걸린다(오차 중앙 1.00m).
const _c4 = [0, 0, 0, 0];
const mipAt = (e, ix, iz) => {
  const mp = e.mip, st = mp.stride;
  // mip 칸 m 은 격자 [m·st, m·st+st-1] 을 대표하고 그 중심은 m·st + (st-1)/2 다.
  const fx = (ix - (st - 1) / 2) / st, fz = (iz - (st - 1) / 2) / st;
  const i0 = Math.max(0, Math.min(mp.w - 1, Math.floor(fx)));
  const j0 = Math.max(0, Math.min(mp.h - 1, Math.floor(fz)));
  const i1 = Math.min(mp.w - 1, i0 + 1), j1 = Math.min(mp.h - 1, j0 + 1);
  const tx = Math.max(0, Math.min(1, fx - i0)), tz = Math.max(0, Math.min(1, fz - j0));
  const a = e.dec(mp.avg[j0 * mp.w + i0]), b = e.dec(mp.avg[j0 * mp.w + i1]);
  const c = e.dec(mp.avg[j1 * mp.w + i0]), d = e.dec(mp.avg[j1 * mp.w + i1]);
  // 한 귀퉁이라도 빈 땅이면 보간이 물가에서 튄다 — 그냥 가장 가까운 유효값을 쓴다.
  if (Number.isNaN(a) || Number.isNaN(b) || Number.isNaN(c) || Number.isNaN(d)) {
    for (const v of [a, b, c, d]) if (!Number.isNaN(v)) return v;
    return NaN;
  }
  return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
};

/** (ix,iz) 셀의 4코너 높이를 `_c4` 에 담는다. 하나라도 빈 땅이면 false. */
const cell4 = (e, ix, iz) => {
  const { meta, dec } = e;
  if (e.mode !== 'tiled') {
    const hs = e.heights, i0 = iz * meta.w + ix;
    _c4[0] = dec(hs[i0]); _c4[1] = dec(hs[i0 + 1]);
    _c4[2] = dec(hs[i0 + meta.w]); _c4[3] = dec(hs[i0 + meta.w + 1]);
  } else {
    // 1셀 겹쳐 구웠으므로 네 코너는 **언제나 한 타일 안**이다(경계 분기가 없다).
    const t = e.tiles.get(tidOf(e, (ix / HF_TILE) | 0, (iz / HF_TILE) | 0));
    if (t) {
      const i0 = (iz % HF_TILE) * t.tw + (ix % HF_TILE);
      _c4[0] = dec(t.a[i0]); _c4[1] = dec(t.a[i0 + 1]);
      _c4[2] = dec(t.a[i0 + t.tw]); _c4[3] = dec(t.a[i0 + t.tw + 1]);
    } else {
      _c4[0] = mipAt(e, ix, iz); _c4[1] = mipAt(e, ix + 1, iz);
      _c4[2] = mipAt(e, ix, iz + 1); _c4[3] = mipAt(e, ix + 1, iz + 1);
    }
  }
  return !(Number.isNaN(_c4[0]) || Number.isNaN(_c4[1]) || Number.isNaN(_c4[2]) || Number.isNaN(_c4[3]));
};

// ── 복셀지형(계단) 밴드 — 렌더/발밑 공용 ──
// 0 = off(원본 매끈). >0 이면 hfSurfaceYAt 가 렌더(buildSteppedGeometry)와 **같은 규칙**으로
// 셀 4코너 평균을 양자화해 돌려준다 → 눈에 보이는 계단 위에 캐릭터가 정확히 선다.
// (기존엔 렌더만 계단이고 발밑은 매끈이라 최대 ±밴드/2 만큼 뜨거나 묻혔음.)
// 주의: 렌더의 원거리 LOD(FAR_STRIDE)는 셀을 건너뛰므로 원경 계단은 살짝 다를 수 있으나,
// 카메라 근처(NEAR_R 이내)는 stride 1 이라 발밑과 완전히 일치한다.
let hfBand = 0;
export const setHfBand = (m) => { const v = Number(m); hfBand = Number.isFinite(v) && v > 0 ? v : 0; };
export const getHfBand = () => hfBand;

// ── 근경 지형이 실제로 깔려 있는 반경 ─────────────────────────────
// [왜 여기 사는가] `FarTerrain` 의 「근경이 이미 그리는 자리는 버린다」 판정은 **근경 지형의
//   실제 반경과 같은 수**라야 한다. 그런데 그 수(`HeightfieldPoC.TILE_ACTIVE_R`)는 기기
//   프로필·「지형 타일 반경」 슬라이더로 매번 바뀌는 모듈 변수다. 상수를 복제하면 슬라이더를
//   움직인 순간 둘이 갈리고, 갈린 만큼이 **겹치는 띠**로 보인다.
//   그래서 값을 복제하지 않고 **여기 한 곳에 두고 양쪽이 읽는다** — 두 파일이 이미 다 이
//   모듈을 import 하고 있어 순환도 안 생긴다(`FarCity` 가 근경 컷 식을 그대로 다시 푸는 것과
//   같은 수법).
// [0 은 「모른다」다] 근경 타일이 아직 하나도 안 잡힌 동안엔 0 을 유지한다. 그때 원경이
//   넓게 잘라내면 근경도 없고 원경도 없는 **2km 짜리 구멍**이 난다 — 「모른다」와 「없다」를
//   가르는 그 규칙(pipeline_sop §10)이 여기도 그대로 걸린다.
// 근경 지형 가장자리를 노이즈 디더로 흩는 폭(m). **0 = 끔**(설정 토글).
// [2026-08-22 재도입] 한 번 넣었다가 되돌린 적이 있다 — 그때는 원경 컷에 폴백 하한이 끼어
//   흩기 구간이 컷보다 안쪽이 되어 **구멍이 뚫렸다.** 이제 원경은 `getHfNearR() - getHfRim()`
//   부터 그리므로 톱니 사이를 반드시 메운다. 두 수가 이 한 곳에서 나온다 — 갈릴 자리가 없다.
let hfRim = 0;
export const setHfRim = (m) => { const v = Number(m); hfRim = Number.isFinite(v) && v > 0 ? v : 0; };
export const getHfRim = () => hfRim;

let hfNearR = 0;
export const setHfNearR = (m) => { const v = Number(m); hfNearR = Number.isFinite(v) && v > 0 ? v : 0; };
export const getHfNearR = () => hfNearR;

// ── 하이트필드 메시가 «지금 그려지고 있나» ────────────────────────
// [왜 필요한가] LAYERS 「지형」을 끄면(블록지형 단독 모드) 하이트필드는 **데이터·발밑만**
//   남고 메시를 안 그린다. 그런데 위 `hfNearR` 은 여전히 타일 반경(기본 2,048m)을 들고 있어서,
//   원경(`FarTerrain`)이 「근경이 이미 그린다」고 믿고 그 안을 통째로 버린다 →
//   **블록 반경 밖 ~2km 가 아무것도 없는 구멍**이 된다.
// [그래서 사실 하나만 더 실어 보낸다] 「메시가 꺼져 있다」. 원경은 그때 근경 반경 대신
//   **블록층이 선 반경**(`terrainVoid`)을 컷으로 쓴다. 값을 복제하지 않고 사실만 나른다 —
//   `hfNearR` 이 값 복제를 피하려고 여기 사는 것과 같은 이유다.
let hfMeshOn = true;
export const setHfMeshOn = (on) => { hfMeshOn = !!on; };
export const getHfMeshOn = () => hfMeshOn;

// 경사 적응 서브셀 — 렌더(buildSteppedGeometry)가 급경사 셀을 s×s 로 쪼개 각각 양자화하므로
// 발밑도 **똑같이** 쪼개 봐야 한다. 안 그러면 서브셀을 켠 순간 렌더는 계단이 잘게 나뉘는데
// 발밑은 셀 하나의 평균만 보고 있어, 캐릭터·몬스터·배치 오브젝트가 지형에 파묻히거나 뜬다
//.
// 경사 적응 밴드 — 렌더(hfBuild.buildSteppedArrays)에 넘기는 값과 **반드시 같아야** 한다.
// 두 값 모두 0/1 이면 종전과 완전히 동일(가역). HeightfieldPoC 가 한 곳에서 둘 다 세팅한다.
let hfFlatten = 0, hfSteepMul = 1;
export const setHfShape = (flatten, steepMul) => {
  const f = Number(flatten); hfFlatten = Number.isFinite(f) && f > 0 ? f : 0;
  const m = Number(steepMul); hfSteepMul = Number.isFinite(m) && m > 1 ? m : 1;
};

/** 「완경사 평탄화 · 급경사 밴드배수」의 **지금 값**. 사전 굽기 지문이 이걸 읽는다
 *  (`build/terrainBakeSig.js` §얼린 것). 세터만 있고 게터가 없으면 굽는 쪽이 「무슨 값으로
 *  구웠나」를 적을 수가 없고, 그러면 지문이 «가짜»가 된다 — 늘 통과하는 지문은 없는 것만 못하다. */
export const getHfShape = () => ({ flatten: hfFlatten, steepMul: hfSteepMul });

let hfSub = 1, hfSubSlope = 0.25;
export const setHfSubdiv = (sub, subSlope) => {
  const v = Math.max(1, Math.round(Number(sub) || 1));
  hfSub = v;
  const t = Number(subSlope);
  if (Number.isFinite(t) && t > 0) hfSubSlope = t;
};

// 「도로서브셀」 — 도로 셀만의 답판 분할. 1 이면 roadCellAt 조회 자체를 건너뛴다
// (발밑은 매 프레임 개체마다 불리므로 꺼진 경로에 비용이 붙으면 안 된다).
let hfRoadSub = 1;
export const setHfRoadSub = (sub) => { hfRoadSub = Math.max(1, Math.round(Number(sub) || 1)); };
/** 경사 적응 서브셀의 지금 값 — 위 `getHfShape` 와 같은 이유(굽기 지문). */
export const getHfSubdiv = () => ({ sub: hfSub, subSlope: hfSubSlope });
export const getHfRoadSub = () => hfRoadSub;
// 도로 답판 «목표 잔단»(m) — 렌더(hfBuild)와 **같은 값**이어야 발이 도로 위에 정확히 선다.
let hfRoadStepTarget = ROAD_STEP_TARGET_M;
export const setHfRoadStepTarget = (m) => {
  const v = Number(m);
  hfRoadStepTarget = Number.isFinite(v) && v >= 0 ? v : ROAD_STEP_TARGET_M;
};
export const getHfRoadStepTarget = () => hfRoadStepTarget;

// 복셀지형 격자 오프셋 — 건축 1m 블록과 계단 모서리를 맞추는 반 칸(0.5m).
//   하이트필드 셀 경계 : origin_x(-15448) + 8i        → 정수 월드좌표
//   건축 블록 경계     : worldToGrid 가 XZ 를 round → 블록 "중심"이 정수 → 경계는 x.5
//   → 그대로 두면 계단 모서리가 블록 한가운데를 가른다(0.5m 어긋남).
// 블록 18,046개를 옮기는 대신 **복셀지형 격자만** 반 칸 밀어 경계를 겹친다.
// 복셀지형 OFF 면 0 이라 기존 지형·오브젝트·도로는 전혀 안 움직인다.
// (VOXEL_GRID_OFF 값은 상단 hfConstants re-export 참조 — SSOT.)

export const setHfPoCActive = (on) => {
  hfActive = !!on;
  // 글로벌 단일맵: 활성화 시 한 장(seoul.global)만 로드한다. 발밑 조회(hfSurfaceYAt)는
  // 이 한 엔트리를 이중선형으로 읽어 1 lookup — 45타일 순회가 사라진다. 렌더도 이 격자를
  // 슬라이스하므로 렌더/발밑이 같은 배열을 본다(§5-C 동기화).
  if (hfActive) ensureHfLoaded(GLOBAL_HF_KEY);
};

// ── 「생 DEM」 조회 — 밴드도 서브셀도 마스크도 안 탄 이중선형 원면 ──
//
// [왜 따로 있나] 도로 정지(`roadGrade.js`)가 **자기 입력**으로 이걸 쓴다. 아래
//   `hfSurfaceYAt` 을 쓰면 되먹임이다 — 그 함수는 결과에 정지값을 이미 섞으므로,
//   그걸 다시 평균 내면 정지가 매 타일마다 또 걸린다(terrainEdits 의 `natural` 과 같은 함정).
// [왜 그래도 «한 곳»인가] 여기는 양자화 식이 **없다**. 격자 조회(cell4)와 이중선형뿐이라
//   SOP §5-C 가 말하는 「복제하면 갈라지는 식」이 애초에 안 들어 있다.
// ⚠ 렌더·발밑이 이걸 직접 쓰면 안 된다. 그건 「복셀지형 OFF」와 같은 값이라 계단이 사라진다.
/** @returns {number|null} 월드 Y(BASE_Y·렌더오프셋 포함). 커버리지 밖이면 null. */
export const hfRawYAt = (wx, wz) => {
  if (!hfActive) return null;
  for (const e of cache.values()) {
    if (!e || typeof e === 'string') continue;
    const { meta } = e;
    // 격자는 `hfSurfaceYAt` 과 **같은 반 칸 오프셋**을 탄다 — 안 그러면 정지값이 지형과
    // 반 칸 어긋난 자리에서 뽑혀 길이 옆으로 밀린다.
    const gOff = hfBand > 0 ? VOXEL_GRID_OFF : 0;
    const fx = (wx - gOff - meta.origin_x) / meta.cell;
    const fz = (wz - gOff - meta.origin_z) / meta.cell;
    const ix = Math.floor(fx), iz = Math.floor(fz);
    if (ix < 0 || iz < 0 || ix >= meta.w - 1 || iz >= meta.h - 1) continue;
    if (!cell4(e, ix, iz)) continue;
    const h00 = _c4[0], h10 = _c4[1], h01 = _c4[2], h11 = _c4[3];
    const tx = fx - ix, tz = fz - iz;
    const h = h00 * (1 - tx) * (1 - tz) + h10 * tx * (1 - tz)
            + h01 * (1 - tx) * tz + h11 * tx * tz;
    return meta.base_y + h + HF_RENDER_OFFSET;
  }
  return null;
};

// ── 도로 정지 주입 ────────────────────────────────────────────────
// [왜 import 가 아니라 주입인가] `roadGrade.js` 가 위 `hfRawYAt` 을 import 한다. 여기서
//   거꾸로 import 하면 순환이 된다. ESM 이 순환을 견디긴 하지만 **초기화 순서에 매달리는
//   코드**가 되고, 이 파일은 매 프레임 개체마다 불리는 자리라 그런 취약함을 두면 안 된다.
//   그래서 화살표를 한 방향으로 고정한다 — roadGrade → heightfieldData, 그리고 값은 주입.
// (`road_sink_hybrid_system.md` 의 `setRoadSinkSampler` 가 같은 이유로 같은 모양이다.)
let roadGradeFn = null;
/** @param {((wx:number, wz:number)=>number|null)|null} fn 정지된 «생높이» 조회. null 이면 무동작. */
export const setRoadGradeSampler = (fn) => { roadGradeFn = typeof fn === 'function' ? fn : null; };

// 월드 좌표 → 하이트필드 표면 Y(월드, BASE_Y 포함). 비활성/커버리지 밖/모서리 NaN 이면 null.
// feetY 를 주면 **발 아래에서 가장 가까운 흙의 윗면**을 찾는다(동굴 바닥에 서기 위함).
// 안 주면 지금까지처럼 맨 위 지표를 준다 — 오브젝트·카메라·풀 스냅은 그대로 동작.
//
// out 을 주면 `out.orig` 에 **깎이기 전 원지표 Y**(밴드 양자화된 값)를 담아 준다.
// 굴착이 이걸 쓴다 — 컬럼 마스크의 비트 인덱스가 「원지표 아래 몇 번째 칸인가」라서,
// 발 높이를 밴드 인덱스로 바꾸려면 원지표가 있어야 한다. 별도 함수로 빼지 않은 이유는
// 양자화 식(terrainBandY + 서브셀 + 도로)이 **여기 한 곳**에만 있어야 하기 때문이다 —
// 복제하면 이 프로젝트가 세 번 겪은 「렌더/발밑 갈라짐」이 굴착에서 한 번 더 난다(SOP §5-C).
/** @param {boolean} natural 조각 편집(쌓기)을 **빼고** 잰다 — 지형 청크 밭이 자기 바탕을
 *  만들 때만 쓴다. 안 빼면 자기 결과를 자기 입력으로 먹어 지형이 자란다(terrainEdits.maskAt ⚠). */
// 지표 대역 — VFX 라이브러리처럼 지형 없이 효과를 띄우는 페이지만 건다. null(기본)이면 아래 조회 그대로.
let hfSurfaceOverride = null;
/** @param {((wx:number, wz:number) => number|null) | null} fn */
export const setHfSurfaceOverride = (fn) => { hfSurfaceOverride = fn || null; };

export const hfSurfaceYAt = (wx, wz, feetY = null, out = null, natural = false) => {
  if (out) { out.orig = null; out.mk = null; out.natOrig = null; out.uniM = 0; }
  if (hfSurfaceOverride) return hfSurfaceOverride(wx, wz);
  if (!hfActive) return null;
  // «균일 구간» 보고용 — 앞 항목을 하나라도 건너뛰었으면 이 셀 안에서도 다른 그룹이 이길 수 있다.
  let skipped = false;
  // 로드된(활성 스트리밍) 그룹만 순회 — HF_GROUPS 45개 전체가 아니라 현재 동+이웃 몇 개.
  for (const e of cache.values()) {
    if (!e || typeof e === 'string') { skipped = true; continue; }
    const { meta } = e;
    // 복셀지형 ON 이면 렌더(buildSteppedGeometry)와 같은 반 칸 오프셋 격자를 쓴다 → 발밑 완전 일치.
    const gOff = hfBand > 0 ? VOXEL_GRID_OFF : 0;
    const fx = (wx - gOff - meta.origin_x) / meta.cell;
    const fz = (wz - gOff - meta.origin_z) / meta.cell;
    const ix = Math.floor(fx), iz = Math.floor(fz);
    if (ix < 0 || iz < 0 || ix >= meta.w - 1 || iz >= meta.h - 1) { skipped = true; continue; }
    // dec 는 u16 고정소수점을 실수로 되돌린다(f32 원본이면 항등). 센티널은 NaN 으로 나오므로
    // 아래 NaN 검사는 인코딩과 무관하게 그대로 성립한다.
    // 통짜/타일/mip 분기는 cell4 **한 곳**에만 있다 — 렌더와 발밑이 갈리지 않는 이유다.
    if (!cell4(e, ix, iz)) { skipped = true; continue; }
    const h00 = _c4[0], h10 = _c4[1], h01 = _c4[2], h11 = _c4[3];
    // 복셀지형 ON: 렌더와 동일하게 셀 4코너 평균 → 밴드 양자화(이중선형 보간 안 함).
    // HeightfieldPoC.buildSteppedGeometry 의 cellBand() 와 같은 식이어야 발밑이 정확히 일치한다.
    const tx0 = fx - ix, tz0 = fz - iz;
    if (hfBand > 0) {
      // 렌더와 동일: 셀 경사가 임계 초과면 s×s 서브블록, 아니면 셀 하나.
      const slope = Math.hypot(((h10 + h11) - (h00 + h01)) / 2 / meta.cell,
                               ((h01 + h11) - (h00 + h10)) / 2 / meta.cell);
      // 렌더(hfBuild.getCell)와 **같은 규칙**이어야 한다 — 도로 셀은 답판을 더 잘게 쪼개므로
      // 여기서도 같이 쪼개지 않으면 도로 위에서 캐릭터가 묻히거나 뜬다(SOP §5-C, 사고 3회).
      let s = (hfSub > 1 && slope > hfSubSlope) ? hfSub : 1;
      // 경사 적응 — 렌더(hfBuild.getCell)와 **같은 함수·같은 인자**여야 한다.
      // 셀 폭은 여기선 meta.cell 고정이다(발밑은 LOD 를 모르고 늘 잔 격자를 본다 — 렌더의
      // stride 1 가드와 짝이라 근경에서는 두 값이 일치한다).
      if (hfRoadSub > 1 && roadCellAt(meta, wx, wz)) {
        s = Math.max(s, roadSubFor(slope, meta.cell, hfRoadSub, hfRoadStepTarget));
      }
      let hv;
      if (s === 1) {
        hv = (h00 + h10 + h01 + h11) / 4;                     // 셀 4코너 평균
      } else {
        const bi = Math.max(0, Math.min(s - 1, Math.floor(tx0 * s)));
        const bj = Math.max(0, Math.min(s - 1, Math.floor(tz0 * s)));
        const u = (bi + 0.5) / s, v = (bj + 0.5) / s;         // 서브블록 중심 이중선형
        hv = (h00 * (1 - u) + h10 * u) * (1 - v) + (h01 * (1 - u) + h11 * u) * v;
      }
      // 128비트 마스크는 값으로 못 받는다 → {셀 배열 m, 컬럼 k}. 없으면 null(=원지형).
      const mk = maskAt(meta, wx, wz, natural);
      const raw = mk;
      // ★ 도로 정지 — 길 위면 «생높이»를 통째로 갈아끼운다.
      //   양자화 «전»에 끼우는 게 핵심이다. 뒤에 끼우면 밴드 정수배를 벗어나 블록이 어긋난다.
      //   렌더(hfBuild.getCell)가 **같은 자리에서 같은 값**을 끼운다 — 한쪽만 하면 캐릭터가
      //   길 위에서 묻히거나 뜬다(SOP §5-C, 사고 3회).
      // ⚠ 판 자리(mk)·정지 필지(raw)에서는 안 끼운다 — 거기선 상판이 밴드 정수배여야 하고,
      //   굴착 마스크의 비트 인덱스가 「원지표 아래 몇 칸」이라 기준면을 옮기면 굴이 어긋난다.
      let rawY = meta.base_y + hv + HF_RENDER_OFFSET;
      let graded = false;
      if (roadGradeFn && !raw) {
        const g = roadGradeFn(wx, wz);
        if (g != null) { rawY = g; graded = true; }
      }
      // 지층 단구 — 표본점은 칸(서브칸) 중심이라 한 칸 안에서 값이 안 변한다(균일 구간 보고와 맞물림).
      if (strataMask && !raw && !graded && slope > strataParams.slopeLo) {
        const sx = meta.origin_x + gOff + (s === 1 ? ix + 0.5 : ix + (Math.max(0, Math.min(s - 1, Math.floor(tx0 * s))) + 0.5) / s) * meta.cell;
        const sz = meta.origin_z + gOff + (s === 1 ? iz + 0.5 : iz + (Math.max(0, Math.min(s - 1, Math.floor(tz0 * s))) + 0.5) / s) * meta.cell;
        const cx = meta.origin_x + gOff + (ix + 0.5) * meta.cell, cz = meta.origin_z + gOff + (iz + 0.5) * meta.cell;
        rawY = strataTerraceY(rawY, cx, cz, slope, strataAmtAt(sx, sz), strataParamsAt(cx, cz));
      }
      const band0 = terrainBandY(rawY, slope, hfBand,
                                raw ? 0 : hfFlatten, raw ? 1 : hfSteepMul);
      // ★ 기준면 올림 — 조각 편집으로 «쌓은» 흙. 렌더(hfBuild.promoteCell)가
      //   **같은 덧셈**을 한다. 한쪽만 하면 쌓은 흙 위에서 캐릭터가 뜨거나 묻힌다(SOP §5-C).
      //   곡괭이 편집(`up` 없음)에서는 0 이라 종전과 글자 그대로 같다.
      // 굴착 비트의 기준면은 하위칸이 아니라 «셀 4코너 평균»이다 — 서버가 그 값으로 판다
      // (`place_cave_dungeon.surface_ref`). 하위칸 값으로 세면 급경사 아래 굴 바닥이
      // 하위칸마다 1~3m 씩 계단진다. 겉면(lead=0 의 상판)은 하위칸 그대로 둔다.
      const ref0 = (mk && s !== 1)
        ? terrainBandY(meta.base_y + (h00 + h10 + h01 + h11) / 4 + HF_RENDER_OFFSET, slope, hfBand, 0, 1)
        : band0;
      const up = ((mk && mk.up) || 0) * hfBand;
      const band = band0 + up;
      const ref = ref0 + up;
      if (out) {
        out.orig = ref;                              // 마스크 비트 인덱스의 기준면
        // ★ **올리기 «전»** 값 — 조각 편집 컴파일러가 이걸 쓴다.
        //   `orig` 를 쓰면 자기가 올린 결과를 다시 입력으로 먹어 **매 컴파일마다 또 올린다**
        //   (기준면이 무한정 올라가 지형이 하늘로 자란다). 두 값을 반드시 갈라 둬야 한다.
        out.natOrig = ref0;
        // 굴착 마스크 자체도 같이 내준다 — 부르는 쪽이 `maskAt` 을 «또»
        // 부르면 셀 조회가 두 번이고, 무엇보다 **어느 셀·어느 컬럼인가**를 바깥에서
        // 다시 계산하게 된다. 그 계산이 갈리면 「렌더/발밑 갈라짐」이 한 벌 더 생긴다.
        // 지형 청크 밭(terrainChunkStore)이 이걸로 «흙 밑 빈 칸»을 읽는다.
        out.mk = mk;                                 // {m, k} 또는 null(=판 적 없음)
        // ★ 「균일 구간」 — 이 DEM 셀 안에서 이 함수는 «같은 값»을 돌려준다.
        //
        // [왜 여기서 보고하나] LOD 격자(`terrainLodStore.fillLattice`)가 이 함수를 1m
        //   간격으로 부른다 — 8m 셀 하나에 64번이고 그중 63번은 같은 숫자를 다시
        //   계산하는 것이다. 그렇다고 부르는 쪽이 「상수인가」를 스스로 판정하면 판정식이
        //   두 벌이 되고, 그게 곧 이 리포가 세 번 겪은 「렌더/발밑 갈라짐」이다(SOP §5-C).
        //   그래서 **값을 낸 자리가 같이 낸다.**
        // [참이 되는 조건 다섯] 서브셀 분할 없음(s=1) · 굴착 마스크 없음(mk=null) ·
        //   **이 셀에 편집 기록이 아예 없음**(cellEdited=false) · 도로 정지 꺼짐(roadGradeFn=null) ·
        //   앞서 건너뛰는 그룹이 없음(skipped=false).
        //   하나라도 깨지면 `uniM = 0`(=「1m 마다 물어라」)로 남아 종전과 글자 그대로 같다.
        // ⚠ **`mk` 만으로는 모자란다** — `mk` 는 «이 컬럼»이고 보고하는 구간은 «이 셀»이다.
        //   한 셀 안에 판 컬럼과 안 판 컬럼이 섞이면, 안 판 컬럼이 셀 전체를 상수라고 보고하고
        //   그 값이 판 컬럼 위로 덮인다 → **판 협곡이 LOD 층에서 다시 메워진다**
        //   (2026-09-11 실측 · 조각 하나의 제 칸 256 중 22개가 1~3m 높았다).
        // [구간] 월드 x ∈ [uniX0, uniX0+uniM) · z ∈ [uniZ0, uniZ0+uniM).
        //   식은 위 `fx`/`ix` 의 역이다 — 그 두 줄과 반드시 같이 움직여야 한다.
        if (s === 1 && !mk && !roadGradeFn && !skipped && !cellEdited(meta, wx, wz)) {
          out.uniM = meta.cell;
          out.uniX0 = meta.origin_x + gOff + ix * meta.cell;
          out.uniZ0 = meta.origin_z + gOff + iz * meta.cell;
        }
      }
      // 굴착(셀 승격) — 판 만큼 발밑도 내려간다. 렌더의 promoteCell 과 같은 규칙이어야
      // 구덩이 바닥에 발이 선다. 렌더만 고치고 발밑을 안 따라가게 두는 건 이 프로젝트가
      // 세 번 겪은 사고다(커튼월·밴드 양자화·서브셀 — SOP §5-C).
      if (!mk) return band;
      const lead = colLead(mk.m, mk.k);
      const top = lead > 0 ? ref - lead * hfBand : band;   // 지표(선두 런만큼 내려간 높이)
      // 동굴이 없거나, 발이 지표 위에 있으면 지금까지와 동일.
      // 발 높이는 캐릭터 공간(지표 −0.05)으로 온다 — 지표에 선 발을 «지표 아래»로 읽지 않게 여유를 둔다.
      if (feetY == null || !colCaveBelow(mk.m, mk.k, lead) || feetY > top - FEET_ON_TOP_EPS) return top;
      // 발 근처에서 첫 solid 칸의 윗면을 찾는다. 원지표 기준 깊이 index 로 훑는다.
      // [발보다 한 계단 **위**부터 훑는 이유 — 2026-08-02] 예전엔 발 높이에서 시작해 아래로만
      //   봤다. 그러면 굴 안에서 **내려가기만 되고 못 올라간다**: 램프 다음 칸 바닥이 1m 높으면
      //   그 칸의 발 높이 밴드는 (바닥 아래라) 흙이므로 지금 높이가 그대로 답이 되고, 캐릭터가
      //   램프에 1m 파묻힌 채 걷는다. bit d 의 윗면은 orig - d·band 이므로
      //   윗면 ≤ feetY + CAVE_STEP_UP 을 만족하는 **가장 높은 칸**부터 보면 한 계단은 올라선다.
      const orig = ref;
      let d = Math.max(lead, Math.ceil((orig - feetY - CAVE_STEP_UP) / hfBand));
      for (; d < CAVE_BITS; d++) {
        // 이 칸이 흙 → 그 윗면이 바닥. 비트 기준면(셀 평균)이 하위칸 지표보다 높을 수 있어 지표 위로는 안 올린다.
        if (colBit(mk.m, mk.k, d) === 0) return Math.min(top, orig - d * hfBand);
      }
      return orig - CAVE_BITS * hfBand;              // 끝까지 전부 빔 — 바닥 없음
    }
    const tx = tx0, tz = tz0;
    const h = h00 * (1 - tx) * (1 - tz) + h10 * tx * (1 - tz) + h01 * (1 - tx) * tz + h11 * tx * tz;
    return meta.base_y + h + HF_RENDER_OFFSET;
  }
  return null;
};

/**
 * 「곡괭이가 파기 시작할 깊이」 — 발 높이(feetY)를 그 컬럼의 **원지표 기준 밴드 인덱스**로.
 *
 * 컬럼 마스크의 비트 d 는 「원지표 아래 d 번째 1m 칸」이고, 그 칸의 윗면은 orig − d·band 다.
 * 발이 밴드 d 의 윗면에 서 있으면 이 함수가 d 를 준다 = **발 바로 아래 칸**. 굴착은 거기서
 * 시작해 아래로 내려간다(terrainEdits.digColumn).
 *
 * 지표에 서 있으면 0 이 나와 2026-08-02 이전과 완전히 같다. 굴 안에 서 있을 때만 값이 붙는다.
 */
export const hfDigFromAt = (wx, wz, feetY) => {
  if (hfBand <= 0 || !Number.isFinite(feetY)) return 0;
  const probe = {};
  hfSurfaceYAt(wx, wz, null, probe);
  if (probe.orig == null) return 0;
  return Math.max(0, Math.round((probe.orig - feetY) / hfBand));
};

/**
 * 이 자리 지형이 **굴착으로 깎인 것인가** (원지형이면 false).
 *
 * 낙하·굴 벽 판정이 이걸 묻는다(playerSurface). 원지형(서울 실지형) 위에서는 언제나 false 라
 * 오픈월드 보행이 종전 코드 경로를 그대로 탄다 — 중력·벽을 전역으로 켜면 실지형 경사를 걸어
 * 오르는 이 게임의 이동 자체가 바뀌기 때문에, 굴착이 만든 지형에서만 켠다.
 *
 * 비용: 편집 셀이 하나도 없으면 maskAt 이 첫 줄에서 빠진다(edits.size 0). 있어도 셀 해시 1회다.
 */
export const hfCarvedAt = (wx, wz) => {
  if (!hfActive || hfBand <= 0) return false;
  for (const e of cache.values()) {
    if (!e || typeof e === 'string') continue;
    const { meta } = e;
    const fx = (wx - VOXEL_GRID_OFF - meta.origin_x) / meta.cell;
    const fz = (wz - VOXEL_GRID_OFF - meta.origin_z) / meta.cell;
    if (fx < 0 || fz < 0 || fx >= meta.w - 1 || fz >= meta.h - 1) continue;
    return maskAt(meta, wx, wz) != null;
  }
  return false;
};
